import * as React from "react";
import {
  Spinner,
  NonIdealState,
  Tabs,
  Tab,
  TabPanel,
  Icon,
  Button,
  Dialog,
  Popover,
  PopoverInteractionKind,
  PopoverPosition,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import { Link } from "react-router-dom";
import { PERMISSION_LEVEL_VIEW } from "../../../../constants";
import { UsersPanel } from "../../common/UsersPanel";
import { GroupsPanel } from "../../common/GroupsPanel";
import {
  useFolderPermissionDetails,
  type Folder,
  type UsersData,
  type GroupsData,
  useFolderMutations,
} from "../../../../hooks";
import { useAuthState } from "../../../../context/auth";
import { isFybrAdminUser } from "../../adminRoles";
import { createStore } from "@xstate/store";
import { useSelector } from "@xstate/store/react";

export interface FolderPermissionsFormProps {
  permissionsDialogFolder: Folder | null;
  onClose: () => void;
  users: UsersData["users"];
  groups: GroupsData["groups"];
  permissionTypes: any[];
  userPermissions: any;
  permissions: any[];
  updatePermissionsAsync: (params: {
    folderId: number;
    userPermissions: Map<number, number>;
    groupPermissions: Map<number, number>;
  }) => Promise<any>;
  isUpdatingPermissions: boolean;
  removeUserPermissionAsync: (params: {
    folderId: number;
    userId: number;
    userPermissions: Map<number, number>;
  }) => Promise<any>;
  removeGroupPermissionAsync: (params: {
    folderId: number;
    groupId: number;
    groupPermissions: Map<number, number>;
  }) => Promise<any>;
  isRemovingUserPermission: boolean;
  isRemovingGroupPermission: boolean;
  isFybrAdmin?: boolean;
  inviteUserAction?: () => React.ReactNode;
}

interface PermissionState {
  selectedUserIds: number[];
  selectedGroupIds: number[];
  userPermissions: Map<number, number>;
  groupPermissions: Map<number, number>;
  permissionTypes: any[];
}

export function FolderPermissionsForm(props: FolderPermissionsFormProps) {
  const {
    permissionsDialogFolder,
    onClose,
    users,
    groups,
    userPermissions,
    permissions,
    updatePermissionsAsync,
    isUpdatingPermissions,
    removeUserPermissionAsync,
    removeGroupPermissionAsync,
    isRemovingUserPermission,
    isRemovingGroupPermission,
    inviteUserAction,
  } = props;

  const permissionTypeIdToLevel = (permissionTypeId: number): number => {
    const permissionType = props.permissionTypes.find(
      (pt: any) => pt.id === permissionTypeId
    );
    return permissionType ? permissionType.level : PERMISSION_LEVEL_VIEW;
  };

  const folderPermissionsStore = React.useMemo(
    () =>
      createStore({
        context: {
          selectedUserIds: userPermissions.map(
            (permission: any) => permission.user.id
          ),
          selectedGroupIds: permissions
            .filter((permission: any) => permission.group)
            .map((permission: any) => permission.group.id),
          userPermissions: new Map<number, number>(
            userPermissions.map((permission: any) => [
              permission.user.id,
              permission.access_level || PERMISSION_LEVEL_VIEW,
            ])
          ),
          groupPermissions: new Map<number, number>(
            permissions
              .filter((permission: any) => permission.group)
              .map((permission: any) => [
                permission.group.id,
                permissionTypeIdToLevel(permission.permission_type_id),
              ])
          ),
          permissionTypes: props.permissionTypes,
        },
        on: {
          reset: (context, event: { context: PermissionState }) => {
            return event.context;
          },
          setSelectedUsers: (context, event: { userIds: number[] }) => {
            const userPermissions = new Map(context.userPermissions);
            event.userIds.forEach((userId) => {
              if (!userPermissions.has(userId)) {
                userPermissions.set(userId, PERMISSION_LEVEL_VIEW);
              }
            });
            Array.from(userPermissions.keys()).forEach((userId) => {
              if (!event.userIds.includes(userId)) {
                userPermissions.delete(userId);
              }
            });
            return {
              ...context,
              selectedUserIds: event.userIds,
              userPermissions,
            };
          },
          setSelectedGroups: (context, event: { groupIds: number[] }) => {
            const groupPermissions = new Map(context.groupPermissions);

            event.groupIds.forEach((groupId) => {
              if (!groupPermissions.has(groupId)) {
                const viewPermission = context.permissionTypes.find(
                  (pt: any) => pt.level === PERMISSION_LEVEL_VIEW
                );
                if (viewPermission) {
                  groupPermissions.set(groupId, viewPermission.level);
                }
              }
            });

            Array.from(groupPermissions.keys()).forEach((groupId) => {
              if (!event.groupIds.includes(groupId)) {
                groupPermissions.delete(groupId);
              }
            });

            return {
              ...context,
              selectedGroupIds: event.groupIds,
              groupPermissions: groupPermissions,
            };
          },
          setUserPermission: (
            context,
            event: { userId: number; accessLevel: number }
          ) => {
            const userPermissions = new Map(context.userPermissions);
            userPermissions.set(event.userId, event.accessLevel);

            return {
              ...context,
              userPermissions,
            };
          },
          setGroupPermission: (
            context,
            event: { groupId: number; accessLevel: number }
          ) => {
            const groupPermissions = new Map(context.groupPermissions);
            groupPermissions.set(event.groupId, event.accessLevel);

            return {
              ...context,
              groupPermissions,
            };
          },
          removeUser: (context, event: { userId: number }) => {
            const filteredUserIds = context.selectedUserIds.filter(
              (id: number) => id !== event.userId
            );
            const userPermissions = new Map(context.userPermissions);
            userPermissions.delete(event.userId);

            return {
              ...context,
              selectedUserIds: filteredUserIds,
              userPermissions,
            };
          },
          removeGroup: (context, event: { groupId: number }) => {
            const filteredGroupIds = context.selectedGroupIds.filter(
              (id) => id !== event.groupId
            );
            const groupPermissions = new Map(context.groupPermissions);
            groupPermissions.delete(event.groupId);

            return {
              ...context,
              selectedGroupIds: filteredGroupIds,
              groupPermissions,
            };
          },
        },
      }),
    [userPermissions, permissions, permissionsDialogFolder?.id]
  );

  const { currentUser } = useAuthState();
  const isFybrAdmin = props.isFybrAdmin ?? isFybrAdminUser(currentUser);
  const visibleTabIds = ["users", ...(isFybrAdmin ? ["groups"] : [])];
  const showTabs = visibleTabIds.length > 1;

  const [selectedTabId, setSelectedTabId] = React.useState<string>("users");
  const [removingUserId, setRemovingUserId] = React.useState<number | null>(
    null
  );
  const [removingGroupId, setRemovingGroupId] = React.useState<number | null>(
    null
  );

  const permissionState = useSelector(
    folderPermissionsStore,
    (state) => state.context
  );

  React.useEffect(() => {
    if (!isFybrAdmin && selectedTabId === "groups") {
      setSelectedTabId("users");
    }
  }, [isFybrAdmin, selectedTabId]);

  const handleUserChange = (userIds: number[]) => {
    folderPermissionsStore.send({ type: "setSelectedUsers", userIds });
  };

  const handleGroupChange = (groupIds: number[]) => {
    folderPermissionsStore.send({ type: "setSelectedGroups", groupIds });
  };

  const handleUserRemove = async (userId: number) => {
    if (!permissionsDialogFolder) return;

    setRemovingUserId(userId);
    try {
      await removeUserPermissionAsync({
        folderId: permissionsDialogFolder.id,
        userId,
        userPermissions: permissionState.userPermissions,
      });
      // Only update local state on success
      folderPermissionsStore.send({ type: "removeUser", userId });
    } catch (error) {
      // Error is already handled by the mutation's onError callback
      // Don't update local state on error
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleGroupRemove = async (groupId: number) => {
    if (!permissionsDialogFolder) return;

    setRemovingGroupId(groupId);
    try {
      await removeGroupPermissionAsync({
        folderId: permissionsDialogFolder.id,
        groupId,
        groupPermissions: permissionState.groupPermissions,
      });
      // Only update local state on success
      folderPermissionsStore.send({ type: "removeGroup", groupId });
    } catch (error) {
      // Error is already handled by the mutation's onError callback
      // Don't update local state on error
    } finally {
      setRemovingGroupId(null);
    }
  };

  const handleUserPermissionChange = (userId: number, accessLevel: number) => {
    folderPermissionsStore.send({
      type: "setUserPermission",
      userId,
      accessLevel,
    });
  };

  const handleGroupPermissionChange = (
    groupId: number,
    accessLevel: number
  ) => {
    folderPermissionsStore.send({
      type: "setGroupPermission",
      groupId,
      accessLevel,
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (permissionsDialogFolder) {
      try {
        await updatePermissionsAsync({
          folderId: permissionsDialogFolder.id,
          userPermissions: permissionState.userPermissions,
          groupPermissions: permissionState.groupPermissions,
        });
        onClose();
      } catch (error) {
        // Error is already handled by the mutation's onError callback
        // Dialog remains open so user can see the error toast and retry
      }
    }
  };

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 600px;
      `}
      title={`Edit Folder Permissions for ${permissionsDialogFolder?.label}`}
      isOpen={permissionsDialogFolder !== null}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit}>
        <div
          css={css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            padding: 2rem 2rem 0 1rem;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 0.5rem;
            `}
          >
            <Icon icon="folder-open" size={32} color="var(--neutral-3)" />
            <h2
              css={css`
                color: var(--neutral-3);
                margin: 1rem 0;
              `}
            >
              {permissionsDialogFolder?.label}
            </h2>
            <Popover
              interactionKind={PopoverInteractionKind.HOVER}
              position={PopoverPosition.RIGHT}
              content={
                <div
                  css={css`
                    width: 300px;
                    padding: 1rem;
                    color: var(--neutral-3);
                  `}
                >
                  Users are assigned <strong>Contributor</strong> access by
                  default. Adjust the granted permissions if necessary. <br />
                  <br />
                  Learn more about folder permissions in the{" "}
                  <Link
                    to="/knowledge"
                    target="_blank"
                    rel="noreferrer"
                    css={css`
                      color: var(--blue-3);
                      text-decoration: underline;

                      &:hover {
                        color: var(--blue-2);
                      }
                    `}
                  >
                    Knowledge Base
                  </Link>
                  .
                </div>
              }
            >
              <Icon
                css={css`
                  margin-left: 1rem;
                `}
                icon="help"
              />
            </Popover>
          </div>
          {showTabs && (
            <Tabs
              id="folder-permissions-tabs"
              fill
              selectedTabId={selectedTabId}
              onChange={(newTabId) => {
                const tabId = newTabId as string;
                if (tabId === "groups" && !isFybrAdmin) {
                  return;
                }
                setSelectedTabId(tabId);
              }}
              size="large"
            >
              <Tab id="users" title="Users" icon="user" />
              {isFybrAdmin && <Tab id="groups" title="Groups" icon="people" />}
            </Tabs>
          )}
        </div>
        <TabPanel
          id="users"
          css={css`
            margin: 0;
          `}
          panel={
            <UsersPanel
              isFybrAdmin={isFybrAdmin}
              onSwitchToGroups={
                isFybrAdmin ? () => setSelectedTabId("groups") : undefined
              }
              selectedUserIds={permissionState.selectedUserIds}
              users={users}
              permissionTypes={permissionState.permissionTypes}
              userPermissions={permissionState.userPermissions}
              onUserChange={handleUserChange}
              onUserRemove={handleUserRemove}
              handleUserPermissionChange={handleUserPermissionChange}
              isRemoving={isRemovingUserPermission && removingUserId !== null}
              userAction={inviteUserAction?.()}
            />
          }
          selectedTabId={selectedTabId}
          parentId="folder-permissions-tabs"
        />
        {isFybrAdmin && (
          <TabPanel
            css={css`
              margin: 0;
            `}
            id="groups"
            panel={
              <GroupsPanel
                selectedGroupIds={permissionState.selectedGroupIds}
                groups={groups}
                permissionTypes={permissionState.permissionTypes}
                groupPermissions={permissionState.groupPermissions}
                onGroupChange={handleGroupChange}
                onGroupRemove={handleGroupRemove}
                handleGroupPermissionChange={handleGroupPermissionChange}
                isRemoving={
                  isRemovingGroupPermission && removingGroupId !== null
                }
              />
            }
            selectedTabId={selectedTabId}
            parentId="folder-permissions-tabs"
          />
        )}
        <div
          css={css`
            display: flex;
            justify-content: end;
            align-items: center;
            width: 100%;
            padding-top: 0.5rem;
            border-top: 1px solid var(--neutral-5);
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: end;
              gap: 1rem;
              padding: 0.5rem 1rem 1rem 1rem;
            `}
          >
            <Button
              text="Cancel"
              intent="none"
              onClick={onClose}
              disabled={isUpdatingPermissions}
            />
            <Button
              text={isUpdatingPermissions ? "Saving..." : "Save"}
              disabled={isUpdatingPermissions}
              intent="primary"
              type="submit"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

interface FolderPermissionsDialogProps {
  permissionsDialogFolder: Folder | null;
  onClose: () => void;
  users: UsersData["users"];
  groups: GroupsData["groups"];
  organizationId: number;
  isLoadingUsersAndGroups?: boolean;
  hasUsersAndGroupsError?: boolean;
}

export function FolderPermissionsDialog(props: FolderPermissionsDialogProps) {
  const {
    permissionsDialogFolder,
    onClose,
    users,
    groups,
    organizationId,
    isLoadingUsersAndGroups = false,
    hasUsersAndGroupsError = false,
  } = props;

  const {
    updatePermissionsAsync,
    isUpdatingPermissions,
    removeUserPermissionAsync,
    removeGroupPermissionAsync,
    isRemovingUserPermission,
    isRemovingGroupPermission,
  } = useFolderMutations(organizationId);

  const {
    permissionTypes,
    userPermissions,
    permissions,
    isLoadingPermissionTypes,
    isLoadingUserPermissions,
    permissionTypesError,
    userPermissionsError,
  } = useFolderPermissionDetails(
    organizationId,
    permissionsDialogFolder?.id || null
  );

  if (
    isLoadingPermissionTypes ||
    isLoadingUserPermissions ||
    isLoadingUsersAndGroups
  ) {
    return (
      <Dialog
        className="bp6-dark"
        css={css`
          width: 600px;
        `}
        title={`Edit Folder Permissions for ${permissionsDialogFolder?.label}`}
        isOpen={permissionsDialogFolder !== null}
        onClose={onClose}
      >
        <div
          css={css`
            display: flex;
            justify-content: center;
            padding: 2rem;
          `}
        >
          <Spinner />
        </div>
      </Dialog>
    );
  }

  if (permissionTypesError || userPermissionsError || hasUsersAndGroupsError) {
    return (
      <Dialog
        className="bp6-dark"
        css={css`
          width: 600px;
        `}
        title={`Edit Folder Permissions for ${permissionsDialogFolder?.label}`}
        isOpen={permissionsDialogFolder !== null}
        onClose={onClose}
      >
        <NonIdealState
          icon="error"
          title="Failed to Load Permissions"
          description="Please try again later."
        />
      </Dialog>
    );
  }

  return (
    <FolderPermissionsForm
      permissionsDialogFolder={permissionsDialogFolder}
      onClose={onClose}
      users={users}
      groups={groups}
      permissionTypes={permissionTypes}
      userPermissions={userPermissions}
      permissions={permissions}
      updatePermissionsAsync={updatePermissionsAsync}
      isUpdatingPermissions={isUpdatingPermissions}
      removeUserPermissionAsync={removeUserPermissionAsync}
      removeGroupPermissionAsync={removeGroupPermissionAsync}
      isRemovingUserPermission={isRemovingUserPermission}
      isRemovingGroupPermission={isRemovingGroupPermission}
    />
  );
}
